"use client";

import {memo, useCallback} from "react";

import {useSnapshot} from "valtio";

import {type DateRangePickerInstance} from "@/shared/models/form/datepickers";
import {Calendar, type CalendarDay} from "@/shared/ui/form/datepickers/parts";

interface DualCalendarProps {
    model: DateRangePickerInstance;
    onDayClick: (day: CalendarDay) => void;
    onDayHover: (date: Date) => void;
    onDayLeave: () => void;
    getDayTooltip?: (date: Date) => string | null | undefined;
    getDayClassName?: (date: Date) => string | null | undefined;
    hideOtherMonthDaysContent?: boolean;
}

export const DualCalendar = memo(function DualCalendar({
    model,
    onDayClick,
    onDayHover,
    onDayLeave,
    getDayTooltip,
    getDayClassName,
    hideOtherMonthDaysContent,
}: DualCalendarProps) {
    const snapshot = useSnapshot(model);

    const handlePreviousYear = useCallback(() => model.previousYear(), [model]);
    const handlePreviousMonth = useCallback(() => model.previousMonth(), [model]);
    const handleNextMonth = useCallback(() => model.nextMonth(), [model]);
    const handleNextYear = useCallback(() => model.nextYear(), [model]);

    return (
        <>
            <Calendar
                month={snapshot.viewMonth}
                year={snapshot.viewYear}
                weekdays={snapshot.weekdays}
                days={snapshot.calendarDays}
                showLeftNav={true}
                disableLeftNav={snapshot.isAtMinMonth}
                disablePreviousYear={snapshot.isAtMinYear}
                showRightNav={false}
                onPreviousYear={handlePreviousYear}
                onPreviousMonth={handlePreviousMonth}
                onDayClick={onDayClick}
                onDayHover={onDayHover}
                onDayLeave={onDayLeave}
                getDayTooltip={getDayTooltip}
                getDayClassName={getDayClassName}
                hideOtherMonthDaysContent={hideOtherMonthDaysContent}
            />

            <Calendar
                month={snapshot.secondViewMonth}
                year={snapshot.secondViewYear}
                weekdays={snapshot.weekdays}
                days={snapshot.secondCalendarDays}
                showLeftNav={false}
                showRightNav={true}
                onNextMonth={handleNextMonth}
                onNextYear={handleNextYear}
                onDayClick={onDayClick}
                onDayHover={onDayHover}
                onDayLeave={onDayLeave}
                getDayTooltip={getDayTooltip}
                getDayClassName={getDayClassName}
                hideOtherMonthDaysContent={hideOtherMonthDaysContent}
            />
        </>
    );
});
